import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { MasterControlSettings } from "@/hooks/useMasterControl";
import MCSettingNumber from "./MCSettingNumber";
import MCSettingToggle from "./MCSettingToggle";

type MCSettingsPanelProps = {
  settings: MasterControlSettings | null;
  onSave: (next: MasterControlSettings) => Promise<void>;
  onReload: () => Promise<void>;
};

const DEFAULT_SETTINGS: MasterControlSettings = {
  enableMarketingAgent: false,
  enablePublicistAgent: false,
  enableAutoSync: false,
  ingestionFrequencyMinutes: 60,
  maxWorkflowConcurrency: 5,
};

export function MCSettingsPanel({ settings, onSave, onReload }: MCSettingsPanelProps) {
  const [local, setLocal] = useState<MasterControlSettings | null>(settings);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLocal(settings);
  }, [settings]);

  const resolved = local ?? settings ?? DEFAULT_SETTINGS;

  const isDirty = useMemo(() => {
    if (!settings || !local) return false;
    return (
      settings.enableMarketingAgent !== local.enableMarketingAgent ||
      settings.enablePublicistAgent !== local.enablePublicistAgent ||
      settings.enableAutoSync !== local.enableAutoSync ||
      settings.ingestionFrequencyMinutes !== local.ingestionFrequencyMinutes ||
      settings.maxWorkflowConcurrency !== local.maxWorkflowConcurrency
    );
  }, [local, settings]);

  const ready = Boolean(settings);

  const update = <K extends keyof MasterControlSettings>(key: K, value: MasterControlSettings[K]) => {
    setLocal((prev) => {
      const base = prev ?? settings ?? DEFAULT_SETTINGS;
      return { ...base, [key]: value };
    });
  };

  const handleSave = async () => {
    if (!local) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(local);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleReload = async () => {
    setReloading(true);
    setError(null);
    try {
      await onReload();
      setLocal(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to reload settings.");
    } finally {
      setReloading(false);
    }
  };

  return (
    <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
      <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">System settings</CardTitle>
          <p className="text-sm text-muted-foreground">Owner-only feature flags and runtime limits.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => void handleReload()} disabled={!ready || reloading}>
            {reloading ? "Reloading…" : "Revert"}
          </Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={!ready || !isDirty || saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-sm text-rose-300">{error}</p>}
        {!ready && <p className="text-sm text-muted-foreground">Loading settings…</p>}
        {ready && (
          <div className="grid gap-4 lg:grid-cols-3">
            <MCSettingToggle
              title="Enable Marketing Agent"
              description="Allow marketing automations to run globally."
              value={resolved.enableMarketingAgent}
              onChange={(checked) => update("enableMarketingAgent", checked)}
              disabled={saving}
            />
            <MCSettingToggle
              title="Enable Publicist Agent"
              description="Controls whether publicist workflows are available."
              value={resolved.enablePublicistAgent}
              onChange={(checked) => update("enablePublicistAgent", checked)}
              disabled={saving}
            />
            <MCSettingToggle
              title="Enable Auto Sync"
              description="Automatically trigger ingestion runs when thresholds hit."
              value={resolved.enableAutoSync}
              onChange={(checked) => update("enableAutoSync", checked)}
              disabled={saving}
            />
            <MCSettingNumber
              title="Ingestion frequency (minutes)"
              description="Minimum gap between auto-ingestion passes."
              value={resolved.ingestionFrequencyMinutes}
              min={5}
              max={360}
              step={5}
              onChange={(value) => update("ingestionFrequencyMinutes", Number.isFinite(value) ? value : 0)}
              disabled={saving}
            />
            <MCSettingNumber
              title="Max workflow concurrency"
              description="Upper bound for simultaneous agent workflows."
              value={resolved.maxWorkflowConcurrency}
              min={1}
              max={50}
              onChange={(value) => update("maxWorkflowConcurrency", Number.isFinite(value) ? value : 1)}
              disabled={saving}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MCSettingsPanel;
